import React, { useMemo } from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { TaskAlt, ErrorOutline } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";

import loginBG from "../assets/images/login_background.png";
import Logo from "../components/common/Logo";

const StoragePaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const paymentData = useMemo(() => {
    const customerName = searchParams.get("customerName") || "";
    const status = searchParams.get("status") || "";
    const amount = searchParams.get("amount") || "";
    const reference = searchParams.get("reference") || "";

    return {
      customerName: decodeURIComponent(customerName).replace(/\+/g, " "),
      status,
      amount,
      reference,
    };
  }, [searchParams]);

  const isPaymentSuccessful = paymentData.status.toLowerCase() === "paid";

  const handleClose = () => {
    navigate("/");
  };

  return (
    <Grid
      container
      component="main"
      sx={{
        minHeight: "100vh",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        zIndex: 1,
        padding: { xs: 2, sm: 3 },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${loginBG})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.8,
          zIndex: -1,
        },
      }}
    >
      <Grid
        item
        xs={12}
        sm={10}
        md={7}
        lg={5}
        component={Paper}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          padding: { xs: "30px 15px", sm: "40px 30px" },
          borderRadius: "25px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            maxWidth: "500px",
          }}
        >
          <Logo isTitle={true} isPosition={true} />

          {isPaymentSuccessful ? (
            <>
              <TaskAlt
                sx={{
                  fontSize: 70,
                  color: "#008F6C",
                  marginTop: 2,
                  marginBottom: 1,
                }}
              />

              <Typography
                variant="h1"
                fontWeight={600}
                align="center"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                  color: "#000000",
                }}
              >
                Your payment is successful
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: "#555",
                  marginTop: 1,
                }}
              >
                Thank you for your payment.
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  marginTop: 3,
                  padding: { xs: 2, sm: 3 },
                  borderRadius: "12px",
                  backgroundColor: "rgba(255, 255, 255, 0.60)",

                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",

                  border: "1px solid rgba(255, 255, 255, 0.55)",

                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                }}
              >
                {paymentData.customerName && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      marginBottom: 1.5,
                    }}
                  >
                    <Typography fontWeight={600}>Customer</Typography>

                    <Typography textAlign="right">
                      {paymentData.customerName}
                    </Typography>
                  </Box>
                )}

                {paymentData.reference && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      marginBottom: 1.5,
                    }}
                  >
                    <Typography fontWeight={600}>Reference</Typography>

                    <Typography textAlign="right">
                      {paymentData.reference}
                    </Typography>
                  </Box>
                )}

                {paymentData.amount && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography fontWeight={600}>Amount Paid</Typography>

                    <Typography
                      fontWeight={700}
                      textAlign="right"
                      sx={{ color: "#008F6C" }}
                    >
                      £{Number(paymentData.amount).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#666",
                  marginTop: 2,
                  maxWidth: "420px",
                }}
              >
                Your payment has been received successfully. We will be in
                contact with you with further details shortly.
              </Typography>
            </>
          ) : (
            <>
              <ErrorOutline
                sx={{
                  fontSize: 70,
                  color: "#D32F2F",
                  marginTop: 2,
                  marginBottom: 1,
                }}
              />

              <Typography
                variant="h1"
                fontWeight={600}
                align="center"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                  color: "#000000",
                }}
              >
                Payment was not successful
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: "#555",
                  marginTop: 1,
                }}
              >
                We could not confirm your payment. Please contact us if you
                believe this is incorrect.
              </Typography>

              {paymentData.reference && (
                <Box
                  sx={{
                    marginTop: 3,
                    padding: 2,
                    borderRadius: "12px",
                    backgroundColor: "#f7f7f7",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Typography fontWeight={600}>Reference</Typography>

                  <Typography sx={{ marginTop: 0.5 }}>
                    {paymentData.reference}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              fontWeight: 600,
              fontSize: "17px",
              marginTop: 4,
              width: "160px",
              borderRadius: "20px",
              padding: "10px 16px",
              textTransform: "capitalize",
              backgroundColor: "#076C6B",
              letterSpacing: 1.2,
              "&:hover": {
                backgroundColor: "#065958",
              },
            }}
          >
            Close
          </Button> */}
        </Box>
      </Grid>
    </Grid>
  );
};

export default StoragePaymentPage;
